/**
 * Sistema de relatório de erros para monitoramento em produção
 * Envia erros críticos para o backend para análise
 * Com suporte a source maps para rastreamento detalhado
 */

import { supabase } from '@/integrations/supabase/client';

interface ErrorReport {
  type: 'chunk_error' | 'network_error' | 'runtime_error' | 'service_worker_error';
  message: string;
  stack?: string;
  url?: string;
  userAgent?: string;
  timestamp: string;
  metadata?: Record<string, any>;
  sourceMap?: {
    originalFile?: string;
    originalLine?: number;
    originalColumn?: number;
  };
}

const ERROR_REPORT_THROTTLE = 60000; // 1 minuto
const lastReportTimes = new Map<string, number>();

/**
 * Tenta extrair informações de source map do stack trace
 */
function extractSourceMapInfo(stack?: string): ErrorReport['sourceMap'] {
  if (!stack) return undefined;
  
  // Tentar identificar arquivo original e linha do source map
  // Formato típico: "at Component (https://domain.com/assets/file-hash.js:line:column)"
  const sourceMapMatch = stack.match(/at\s+(\S+)\s+\(([^:]+):(\d+):(\d+)\)/);
  
  if (sourceMapMatch) {
    return {
      originalFile: sourceMapMatch[2],
      originalLine: parseInt(sourceMapMatch[3]),
      originalColumn: parseInt(sourceMapMatch[4])
    };
  }
  
  return undefined;
}

/**
 * Reporta erro para o backend
 */
export async function reportError(report: Omit<ErrorReport, 'timestamp' | 'userAgent' | 'sourceMap'>): Promise<void> {
  // Throttle - não enviar o mesmo tipo de erro múltiplas vezes em curto período
  const throttleKey = `${report.type}-${report.message}`;
  const lastReport = lastReportTimes.get(throttleKey);
  const now = Date.now();
  
  if (lastReport && now - lastReport < ERROR_REPORT_THROTTLE) {
    console.log('⏸️ Erro throttled, não reportando novamente:', report.type);
    return;
  }
  
  lastReportTimes.set(throttleKey, now);
  
  try {
    const sourceMapInfo = extractSourceMapInfo(report.stack);
    
    const fullReport: ErrorReport = {
      ...report,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      sourceMap: sourceMapInfo
    };
    
    console.log('📤 Reportando erro para backend:', fullReport);
    
    const { error } = await supabase.functions.invoke('report-error', {
      body: fullReport
    });
    
    if (error) {
      console.error('❌ Erro ao reportar:', error);
    } else {
      console.log('✅ Erro reportado com sucesso');
    }
  } catch (error) {
    console.error('❌ Falha ao reportar erro:', error);
  }
}

/**
 * Reporta erro de chunk loading
 */
export async function reportChunkError(errorMessage: string, metadata?: Record<string, any>): Promise<void> {
  // Capturar stack trace completo
  const error = new Error(errorMessage);
  
  await reportError({
    type: 'chunk_error',
    message: errorMessage,
    stack: error.stack,
    metadata: {
      ...metadata,
      caches_available: 'caches' in window,
      service_worker_available: 'serviceWorker' in navigator,
      online: navigator.onLine,
      buildTime: import.meta.env.VITE_BUILD_TIMESTAMP || 'unknown'
    }
  });
}

/**
 * Reporta erro de service worker
 */
export async function reportServiceWorkerError(errorMessage: string, metadata?: Record<string, any>): Promise<void> {
  await reportError({
    type: 'service_worker_error',
    message: errorMessage,
    metadata
  });
}

/**
 * Reporta erro de rede
 */
export async function reportNetworkError(errorMessage: string, metadata?: Record<string, any>): Promise<void> {
  await reportError({
    type: 'network_error',
    message: errorMessage,
    metadata: {
      ...metadata,
      online: navigator.onLine,
      connection: (navigator as any).connection?.effectiveType || 'unknown'
    }
  });
}

/**
 * Inicializa monitoramento global de erros
 */
export function initGlobalErrorMonitoring(): void {
  // Capturar erros não tratados
  window.addEventListener('error', (event) => {
    const error = event.error || event.message;
    const errorMsg = typeof error === 'string' ? error : error?.message || '';
    
    // Detectar tipo de erro
    if (
      errorMsg.includes('Failed to fetch dynamically imported module') ||
      errorMsg.includes('Importing a module script failed')
    ) {
      reportChunkError(errorMsg, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stackTrace: error?.stack
      });
    } else if (errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError')) {
      reportNetworkError(errorMsg, {
        filename: event.filename,
        stackTrace: error?.stack
      });
    } else {
      // Reportar erro genérico
      reportError({
        type: 'runtime_error',
        message: errorMsg,
        stack: error?.stack,
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      });
    }
  });
  
  // Capturar promise rejections não tratadas
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    const errorMsg = typeof error === 'string' ? error : error?.message || 'Unhandled Promise Rejection';
    
    if (errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError')) {
      reportNetworkError(errorMsg, {
        stackTrace: error?.stack
      });
    } else {
      reportError({
        type: 'runtime_error',
        message: errorMsg,
        stack: error?.stack
      });
    }
  });
  
  console.log('✅ Monitoramento global de erros iniciado com source maps');
}
